import z from 'zod';
import { PureAbility, RawRuleFrom } from '@casl/ability';

import { AppAbilities, tenantRoles, companyRoles } from './roles';
import { Action } from './action';

type Abilities =
  | AppAbilities<typeof tenantRoles>
  | AppAbilities<typeof companyRoles>;

export type TenantSubject = keyof typeof tenantRoles;
export type CompanySubject = keyof typeof companyRoles;

export type Rule = RawRuleFrom<Abilities, unknown>;

export type AppAbility = PureAbility<Abilities>;

const buildAbilitySchema = (roles: Record<string, Action[]>) =>
  z.union(
    Object.entries(roles).map(([subject, actions]) =>
      z.tuple([z.enum(actions as [string, string]), z.literal(subject)]),
    ) as [z.ZodTuple<any>, z.ZodTuple<any>], // Ensure at least two elements,
  );

// Build Zod schema for validating abilities
const abilitiesSchemas = {
  company: buildAbilitySchema(companyRoles),
  tenant: buildAbilitySchema(tenantRoles),
  backoffice: buildAbilitySchema({}),
};

export class InvalidAbilityError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidAbilityError';
  }
}

// Function to define abilities for a given set of rules
export const defineAbilitiesFor = (
  rules: Rule[] = [],
  mode: 'tenant' | 'company' | 'backoffice',
): AppAbility => {
  const isValid = z
    .array(abilitiesSchemas[mode])
    .safeParse(rules.map(({ action, subject }) => [action, subject]));

  if (!isValid.success) {
    throw new InvalidAbilityError('Invalid ability rules');
  }

  const abilities = new PureAbility<Abilities>(rules);

  // Bind `can` and `cannot` methods for better usability
  abilities.can = abilities.can.bind(abilities);
  abilities.cannot = abilities.cannot.bind(abilities);

  return abilities;
};
